import { computed, reactive, ref, unref } from 'vue';
import { useRoute } from 'vue-router';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import { buildTaxFeeGovernanceRows } from 'dashboard/helper/lais/imovelPresenter';
import {
  TAX_FEE_HISTORY_LIMIT,
  TAX_FEE_SIMULATION_LABELS,
  TAX_FEE_SIMULATION_TOTAL_LABELS,
} from '../utils/imovelShowConstants';
import {
  actionErrorMessage,
  formatBRL,
  formatTaxFeeSimulationTotalValue,
  normalizePayload,
} from '../utils/imovelShowFormatters';

/**
 * Governança de IPTU/condomínio e simulação de taxas e custos do imóvel.
 *
 * @param {object} options
 * @param {import('vue').Ref<object>} options.imovel
 * @param {import('vue').Ref<object>} options.additionalAttributes
 * @param {import('vue').Ref<object>} options.rawAdditionalAttributes
 * @param {Function} options.refreshImovel
 */
export function useImovelTaxFees({
  imovel,
  additionalAttributes,
  rawAdditionalAttributes,
  refreshImovel,
}) {
  const route = useRoute();

  const taxFeeSimulation = ref(null);
  const taxFeeSimulationLoading = ref(false);
  const taxFeeSimulationError = ref('');
  const taxFeeSimulationSuccess = ref('');
  const taxFeeForm = reactive({
    financedValue: '',
    paymentKind: 'a_vista',
    hasSfh: false,
    firstPurchase: false,
    termMonths: 360,
  });

  const taxFeeGovernanceRows = computed(() =>
    buildTaxFeeGovernanceRows(unref(imovel))
  );
  const condominiumFeeRow = computed(() =>
    taxFeeGovernanceRows.value.find(row => row.field === 'valor_condominio')
  );
  const iptuFeeRow = computed(() =>
    taxFeeGovernanceRows.value.find(row => row.field === 'valor_iptu')
  );

  const taxFeeSimulationHistory = computed(() => {
    const records =
      taxFeeSimulation.value?.simulationHistory ||
      unref(additionalAttributes).taxFeeSimulations ||
      unref(rawAdditionalAttributes).tax_fee_simulations ||
      [];

    return Array.isArray(records)
      ? records.slice(-TAX_FEE_HISTORY_LIMIT).reverse()
      : [];
  });

  const taxFeeSimulationRows = computed(() => {
    const fees = taxFeeSimulation.value?.fees || {};

    return Object.entries(fees).map(([key, value]) => ({
      key,
      label: TAX_FEE_SIMULATION_LABELS[key] || key,
      value: formatBRL(value) || '—',
    }));
  });

  const taxFeeSimulationTotalRows = computed(() => {
    const totals = taxFeeSimulation.value?.totals || {};

    return Object.entries(totals).map(([key, value]) => ({
      key,
      label: TAX_FEE_SIMULATION_TOTAL_LABELS[key] || key,
      value: formatTaxFeeSimulationTotalValue(key, value),
    }));
  });

  const taxFeeSimulationPayload = () => ({
    cidade: unref(imovel)?.cidade,
    uf: unref(imovel)?.uf,
    valor_venda: unref(imovel)?.valorVenda,
    financed_value: taxFeeForm.financedValue || undefined,
    payment_kind: taxFeeForm.paymentKind,
    has_sfh: taxFeeForm.hasSfh,
    first_purchase: taxFeeForm.firstPurchase,
    term_months: taxFeeForm.termMonths,
  });

  const calcularTaxasCustos = async () => {
    taxFeeSimulationLoading.value = true;
    taxFeeSimulationError.value = '';
    taxFeeSimulationSuccess.value = '';
    try {
      const { data } = await ImovelAPI.taxFees(
        route.params.id,
        taxFeeSimulationPayload()
      );
      taxFeeSimulation.value = normalizePayload(data || {});
      taxFeeSimulationSuccess.value =
        'Simulação de taxas e custos registrada no histórico.';
      useAlert(taxFeeSimulationSuccess.value);
      await refreshImovel();
    } catch (error) {
      taxFeeSimulationError.value = actionErrorMessage(
        error,
        'Não foi possível calcular taxas e custos.'
      );
      useAlert(taxFeeSimulationError.value);
    } finally {
      taxFeeSimulationLoading.value = false;
    }
  };

  return {
    taxFeeSimulation,
    taxFeeSimulationLoading,
    taxFeeSimulationError,
    taxFeeSimulationSuccess,
    taxFeeForm,
    taxFeeGovernanceRows,
    condominiumFeeRow,
    iptuFeeRow,
    taxFeeSimulationHistory,
    taxFeeSimulationRows,
    taxFeeSimulationTotalRows,
    calcularTaxasCustos,
  };
}

export default useImovelTaxFees;
